import { WebSocketMessage } from '../types';
import { WEBSOCKET_CONFIG, BACKEND_CONFIG } from '../config';

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS;
  private reconnectInterval = WEBSOCKET_CONFIG.RECONNECT_INTERVAL;
  private listeners: Map<string, ((data: any) => void)[]> = new Map();
  private isConnecting = false;
  private seqCounter: number = 1;
  private pingInterval: any = null;
  private readonly PING_INTERVAL = WEBSOCKET_CONFIG.PING_INTERVAL;

  constructor() {
    this.seqCounter = 1;
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      console.log('[WebSocket] Already connected or connecting, skipping...');
      return;
    }

    this.isConnecting = true;
    console.log('[WebSocket] Starting connection...');
    
    // 기존 연결이 있다면 정리
    if (this.ws) {
      console.log('[WebSocket] Cleaning up existing connection...');
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    
    // 설정 파일에서 WebSocket URL 가져오기
    const wsUrl = WEBSOCKET_CONFIG.URL;
    console.log(`[WebSocket] Connecting to: ${wsUrl}`);
    console.log(`[WebSocket] Browser WebSocket protocol version: ${WebSocket.prototype.constructor.name}`);
    console.log(`[WebSocket] User Agent: ${navigator.userAgent}`);

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error('[WebSocket] Failed to create WebSocket:', error);
      this.isConnecting = false;
      this.attemptReconnect();
      return;
    }

    this.ws.onopen = (event) => {
      console.log('[WebSocket] 연결 성공 ✅', event);
      console.log(`[WebSocket] Protocol: ${this.ws?.protocol || 'none'}`);
      console.log(`[WebSocket] Extensions: ${this.ws?.extensions || 'none'}`);
      console.log(`[WebSocket] ReadyState: ${this.ws?.readyState}`);
      this.reconnectAttempts = 0;
      this.isConnecting = false;
      this.startPingInterval();
      this.emit('connected', {});
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        // 새로운 메시지 포맷인지 확인
        if (message.action && message.seq && message.ts) {
          // 새로운 포맷의 메시지
          const wsMessage: WebSocketMessage = message;
          
          console.log(`📥 [WebSocket] 백엔드에서 메시지 수신 → action: ${wsMessage.action}, seq: ${wsMessage.seq}, ts: ${wsMessage.ts}`);
          if (wsMessage.data && Object.keys(wsMessage.data).length > 0) {
            console.log(`📦 [WebSocket] 백엔드 데이터:`, wsMessage.data);
          }
          
          if (wsMessage.action === 'pong') {
            console.log(`📡 [WebSocket] Pong 응답 처리 완료 ✅`);
          } else if (wsMessage.action === 'connection') {
            console.log(`🔌 [WebSocket] 연결 확인 메시지 수신:`, wsMessage.data);
          }
          
          this.emit('message', wsMessage);
          this.emit(wsMessage.action, wsMessage);
        } else {
          // 기존 포맷의 메시지 (호환성)
          console.log('[WebSocket] 기존 포맷 메시지 수신:', message);
          this.emit('message', message);
          
          if (message.type) {
            this.emit(message.type, message);
          }
        }
      } catch (error) {
        console.error('[WebSocket] 메시지 파싱 오류:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocket] 연결 끊어짐 ❌', event);
      console.log(`[WebSocket] Close code: ${event.code}, reason: ${event.reason}`);
      console.log(`[WebSocket] Was clean: ${event.wasClean}`);

      // Close code 해석
      const closeReasons: { [key: number]: string } = {
        1000: 'Normal Closure',
        1001: 'Going Away',
        1002: 'Protocol Error',
        1003: 'Unsupported Data',
        1005: 'No Status Received',
        1006: 'Abnormal Closure',
        1007: 'Invalid frame payload data',
        1008: 'Policy Violation',
        1009: 'Message Too Big',
        1010: 'Mandatory Extension',
        1011: 'Internal Server Error',
        1015: 'TLS Handshake'
      };

      console.log(`[WebSocket] Close reason: ${closeReasons[event.code] || 'Unknown'} (${event.code})`);

      this.isConnecting = false;
      this.stopPingInterval();
      this.emit('disconnected', {});
      this.attemptReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocket] 연결 오류:', error);
      console.error('[WebSocket] Error details:', {
        type: error.type,
        target: error.target,
        currentTarget: error.currentTarget
      });
      this.isConnecting = false;
      this.stopPingInterval();
      this.emit('error', error);
    };
  }

  private attemptReconnect() {
    if (this.maxReconnectAttempts === -1 || this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const attemptText = this.maxReconnectAttempts === -1 
        ? `${this.reconnectAttempts}` 
        : `${this.reconnectAttempts}/${this.maxReconnectAttempts}`;
      console.log(`WebSocket 재연결 시도 ${attemptText}`);
      
      setTimeout(() => {
        this.connect();
      }, this.reconnectInterval);
    } else {
      console.error('WebSocket 재연결 실패: 최대 시도 횟수 초과');
    }
  }

  send(action: string, data?: any): WebSocketMessage {
    // seqCounter가 undefined인 경우 초기화
    if (this.seqCounter === undefined) {
      this.seqCounter = 1;
    }
    
    const message: WebSocketMessage = {
      action,
      seq: `F${this.seqCounter.toString().padStart(5, '0')}`,
      ts: new Date().toISOString().replace('T', ' ').replace('Z', ''),
      data: data || {}
    };
    
    this.seqCounter++;
    
    // 5자리 rotation (F00000 ~ F99999)
    if (this.seqCounter > 99999) {
      this.seqCounter = 0;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log(`📤 [WebSocket] 백엔드로 메시지 전송 → action: ${message.action}, seq: ${message.seq}, ts: ${message.ts}`);
      if (message.data && Object.keys(message.data).length > 0) {
        console.log(`📦 [WebSocket] 백엔드로 전송할 데이터:`, message.data);
      }
      
      this.ws.send(JSON.stringify(message));
      
      if (message.action === 'ping') {
        console.log(`🏓 [WebSocket] Ping 전송 완료 ✅`);
      } else {
        console.log(`📡 [WebSocket] 메시지 전송 완료 ✅`);
      }
    } else {
      console.warn('[WebSocket] 연결되지 않아 메시지 전송 실패');
    }
    
    return message;
  }

  // ping 메시지 전송
  ping() {
    return this.send('ping');
  }

  // 자동 ping 인터벌 시작
  private startPingInterval() {
    this.stopPingInterval(); // 기존 인터벌 정리
    
    console.log(`[WebSocket] 자동 Ping 시작 (${this.PING_INTERVAL/1000}초 간격) ⏰`);
    
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ping();
      }
    }, this.PING_INTERVAL);
  }

  // 자동 ping 인터벌 정지
  private stopPingInterval() {
    if (this.pingInterval) {
      console.log('[WebSocket] 자동 Ping 정지 ⏹️');
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // 이벤트 리스너 등록
  on(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  // 이벤트 리스너 제거
  off(event: string, callback: (data: any) => void) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  // 이벤트 발생
  private emit(event: string, data: any) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => callback(data));
    }
  }

  disconnect() {
    console.log('[WebSocket] 수동 연결 종료');
    this.stopPingInterval();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  getConnectionState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}

export const websocketService = new WebSocketService();
export default websocketService;